import {
  apiFinanceCodeAmount,
  apiFinanceUserCoin,
  apiFinanceUserCoinTransfer,
} from '@/api/fetch-utils/transfer';
import { createFetchClient, refreshApi, useFetch } from '@/lib/fetch-tools';
import { SwapOutlined } from '@ant-design/icons';
import { RekuLayout } from '@components/reku/layout';
import { signal } from '@preact-signals/safe-react';
import { message, Spin } from 'antd';
import { Dialog } from 'antd-mobile';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { getText } from '../../utils/util';
import './transfer.css';

const transferAmountSchema = z
  .string()
  .min(1, {
    message: '请输入有效的划转金额',
  })
  .refine(
    (value) => {
      const numberValue = parseFloat(value);
      return !isNaN(numberValue) && numberValue > 0;
    },
    {
      message: '请输入有效的划转金额',
    }
  );
// type:传1，或者2， 1：总额->交易额，2:交易额->总额
const TRANSFER_TYPES = {
  SPOT_TO_CONTRACT: 1, // 总额->交易额
  CONTRACT_TO_SPOT: 2, // 交易额->总额
} as const;
const transferTypeSchema = z.nativeEnum(TRANSFER_TYPES);
type TransferType = z.infer<typeof transferTypeSchema>;

const transferAmountSignal = signal<string>('');
const transferTypeSignal = signal<TransferType>(
  TRANSFER_TYPES['SPOT_TO_CONTRACT']
);
const showSuccessMessageSignal = signal<boolean>(false);

export default function TransferPage() {
  const navigate = useNavigate();
  const uid = localStorage.getItem('uid');
  const { t: translate } = useTranslation();

  const requestFinanceUserCoin = useFetch(apiFinanceUserCoin, {
    fetchParams: {
      uid: uid,
    },
  });

  const {
    data: { value: codeAmount },
    isLoading: { value: isCodeAmountLoading },
  } = useFetch(apiFinanceCodeAmount, {
    fetchParams: {
      uid: uid,
    },
  });
  const isNeedTip = !codeAmount?.data;

  const isLoading = requestFinanceUserCoin.isLoading.value;
  const userData = requestFinanceUserCoin.data.value?.data || {
    usdt: 0,
    tradeUsdt: 0,
  };
  const availableBalance =
    transferTypeSignal.value === TRANSFER_TYPES['SPOT_TO_CONTRACT']
      ? Number(userData.usdt || 0)
      : Number(userData.tradeUsdt || 0);

  const requestFinanceUserCoinTransfer = createFetchClient(
    apiFinanceUserCoinTransfer
  );

  const { fetch, isLoading: isTransferringLoading } =
    requestFinanceUserCoinTransfer;

  const handleTransfer = async () => {
    if (
      isNeedTip &&
      transferTypeSignal.value === TRANSFER_TYPES['CONTRACT_TO_SPOT']
    ) {
      const result = await Dialog.confirm({
        confirmText: translate(getText('確定')),
        cancelText: translate(getText('取消')),
        title: translate(getText('提示')),
        content: (
          <Trans
            i18nKey={
              '您的交易量尚未完成，当前划转至钱包余额将收取 20%手续费。 是否确认继续划转？'
            }
          >
            <p className="text-center">
              Your trading volume has not been completed yet, and a 20% fee will
              be charged when transferring to the wallet balance.
            </p>
            <p className="text-center">
              Do you confirm to continue transferring?
            </p>
          </Trans>
        ),
      });
      if (result === false) {
        return;
      }
    }

    const result = transferAmountSchema.safeParse(transferAmountSignal.value);
    if (!result.success) {
      message.error(translate(getText(result.error.message)));
      return;
    }

    try {
      const response = await fetch({
        fetchParams: {
          uid: Number(uid),
          amount: parseFloat(transferAmountSignal.value),
          type: transferTypeSignal.value,
        },
      });

      if (response.code === 0) {
        showSuccessMessageSignal.value = true;
        setTimeout(() => (showSuccessMessageSignal.value = false), 3000);
        message.success(translate(getText('划转成功')));
        transferAmountSignal.value = '';
        // 刷新余额数据
        refreshApi(apiFinanceUserCoin);
      } else {
        message.error(response.message || translate(getText('划转失败')));
      }
    } catch (error) {
      console.error('Transfer error:', error);
      message.error(translate(getText('划转失败，请稍后再试')));
    }
  };

  const handleSetMax = () => {
    transferAmountSignal.value = availableBalance.toString();
  };

  return (
    <div className="page bg-gradient-to-b from-neutral-50 to-white min-h-screen">
      <RekuLayout
        title={
          <div className={'flex justify-between items-center'}>
            <div className="font-semibold text-xl">
              {translate(getText('划转'))}
              {isNeedTip ? '1' : '2'}
            </div>
          </div>
        }
      >
        <div className="px-4 sm:px-6 md:px-8 max-w-md mx-auto my-6">
          {showSuccessMessageSignal.value && (
            <div className="success-message fixed top-6 left-1/2 transform -translate-x-1/2 bg-success-500 text-neutral-10 px-6 py-3 rounded-xl shadow-lg z-50 backdrop-blur-sm bg-opacity-90 animate-fadeIn">
              {translate(getText('划转成功'))}
            </div>
          )}

          <div className="bg-white p-5 rounded-2xl mb-6 transition-all duration-300 shadow-sm hover:shadow-md border border-neutral-100 flex items-center">
            <div className="text-sm text-neutral-600">
              {translate(getText('从'))}{' '}
              <span className="font-medium text-neutral-800">
                {transferTypeSignal.value === TRANSFER_TYPES['SPOT_TO_CONTRACT']
                  ? translate(getText('总额'))
                  : translate(getText('交易额'))}
              </span>{' '}
              {translate(getText('划转至'))}{' '}
              <span className="font-medium text-neutral-800">
                {transferTypeSignal.value === TRANSFER_TYPES['SPOT_TO_CONTRACT']
                  ? translate(getText('交易额'))
                  : translate(getText('总额'))}
              </span>
            </div>
            <div className="ml-auto">
              <button
                onClick={() => {
                  transferTypeSignal.value =
                    transferTypeSignal.value ===
                    TRANSFER_TYPES['SPOT_TO_CONTRACT']
                      ? TRANSFER_TYPES['CONTRACT_TO_SPOT']
                      : TRANSFER_TYPES['SPOT_TO_CONTRACT'];
                }}
                className="flex items-center justify-center w-12 h-12 bg-primary-500 rounded-full text-neutral-10 hover:bg-primary-600 transition-all transform hover:scale-105 active:scale-95 duration-200 shadow-md hover:shadow-primary-300/50"
              >
                <SwapOutlined className="text-xl" />
              </button>
            </div>
          </div>

          <div className="mb-5 px-1">
            {isLoading ? (
              <div className="flex items-center text-sm text-neutral-500 mb-1">
                <Spin size="small" className="mr-2" />
              </div>
            ) : (
              <div className="text-sm text-neutral-600 flex items-center">
                {translate(getText('可用'))}:{' '}
                <span className="font-medium text-neutral-800 ml-1">
                  {availableBalance.toFixed(8)} USDT
                </span>
              </div>
            )}
          </div>

          <div className="mb-8 bg-white rounded-xl p-3 flex items-center transition-all duration-300 shadow-sm hover:shadow-md focus-within:ring-2 focus-within:ring-primary-300 border border-neutral-100">
            <input
              type="number"
              value={transferAmountSignal.value}
              onChange={(e) => (transferAmountSignal.value = e.target.value)}
              placeholder={translate(getText('请输入划转数量'))}
              className="bg-transparent border-none outline-none flex-1 p-3 text-lg"
              disabled={isLoading}
            />
            <div className="flex items-center gap-3">
              <span className="text-neutral-700 font-medium">USDT</span>
              <button
                onClick={handleSetMax}
                className="px-4 py-2 text-sm bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-all transform hover:scale-105 active:scale-95 duration-200 text-neutral-800 font-medium"
                disabled={isLoading}
              >
                {translate(getText('全部'))}
              </button>
            </div>
          </div>

          {/* 划转按钮 */}
          <button
            onClick={handleTransfer}
            disabled={
              !transferAmountSignal.value ||
              parseFloat(transferAmountSignal.value) <= 0 ||
              isTransferringLoading.value ||
              isLoading
            }
            className="w-full py-4 bg-primary-500 text-neutral-10 rounded-xl font-medium hover:bg-primary-600 transition-all disabled:bg-neutral-300 disabled:cursor-not-allowed transform hover:scale-[1.01] active:scale-[0.99] duration-200 shadow-lg hover:shadow-primary-300/50 text-lg"
          >
            {isTransferringLoading.value ? (
              <div className="flex items-center justify-center">
                <Spin size="small" className="mr-2" />
                {translate(getText('划转中...'))}
              </div>
            ) : (
              translate(getText('划转'))
            )}
          </button>
        </div>
      </RekuLayout>
    </div>
  );
}
